/**
 * Organization and location-related types
 */

import type { BaseEntity, Status, UUID } from "./common.types";

// Organization type
export interface Organization extends BaseEntity {
  name: string;
  legal_name?: string;
  tax_id?: string;
  type?: string; // Legacy field - kept for compatibility
  industry?: string;
  website?: string;
  email?: string; // Made optional for partner organizations
  phone?: string;
  logo_url?: string;
  status: Status;
  organization_id?: string; // Unique 7-digit alphanumeric identifier
  facility_id?: string; // Facility ID for voucher linking and reporting
  parent_organization_id?: UUID;
  settings?: OrganizationSettings;
  metadata?: Record<string, unknown>;

  // New fields for enhanced organization management
  // Primary Address
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  address_country?: string;

  // Mailing Address (separate from primary)
  mailing_address_line1?: string;
  mailing_address_line2?: string;
  mailing_city?: string;
  mailing_state?: string;
  mailing_postal_code?: string;
  mailing_country?: string;
  // Persisted "Same as physical address" (migration 2026-10-05-302; the DB copies the physical address while true)
  mailing_same_as_physical?: boolean;

  // Organization Types (multi-select)
  organization_types?: OrganizationTypeTag[];

  // Account and business information
  account_number?: string;
  // Territory Numeric Designation as entered, leading zeros kept (migration 2026-10-05-301). Source of truth.
  territory_designation?: string | null;
  // Legacy integer mirror kept in sync by the database for old readers (BI). Do not write it from the app.
  territory_numeric?: number;
  region?: string;
  affiliate_id?: string;
  zuora_link?: string;

  // Tags for city and state (legacy fields)
  city_tag?: string;
  state_tag?: string;
  description?: string;
  tags?: string[];

  // Organization classification and prospect tracking
  organization_type?: OrganizationTypeValue; // Single type: retailer, promoter, partner, range, other
  is_prospect?: boolean; // Whether this is a prospect (can be combined with any status)
  shooting_qualification?: boolean; // Whether this org offers shooting qualification
  promo_code?: string; // Promotional code associated with this organization

  // Smart Admin replacement R2A (2026-05-17): "No Reseller" sentinel.
  // One organizations row exists with is_no_reseller=true. Protected
  // by trigger from delete + rename + flag flip. Selectors pin it as
  // a fixed option for events that take place without a signed partner.
  is_no_reseller?: boolean;

  // Soft-disable lifecycle. Archived organizations remain resolvable by ID
  // for historical records but are excluded from operational discovery.
  is_archived?: boolean;
  archived_at?: string | null;
  archived_by?: UUID | null;
  archived_previous_status?: string | null;

  // Geolocation fields (auto-populated from zip lookup or manual geocoding)
  latitude?: number;
  longitude?: number;
  geocode_source?: GeocodeSource;
  coordinates_updated_at?: string;
}

// Location type
export interface Location extends BaseEntity {
  organization_id: UUID;
  name: string;
  title?: string; // e.g., "Headquarters", "Field Office"
  description?: string;

  // Address fields
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;

  // Contact fields
  phone_number?: string;
  phone?: string; // Alias for phone_number
  email?: string;

  // Location-specific identifiers (may be copied from organization)
  affiliate_id?: string;
  facility_id?: string;

  // Address aliases for compatibility
  address_city?: string;
  address_state?: string;

  // Geolocation
  latitude?: number;
  longitude?: number;

  // Status
  status?: Status;
  is_archived?: boolean;

  // Metadata
  created_by?: UUID;
  metadata?: Record<string, unknown>;
}

// Location assignment (user assigned to location)
export interface LocationAssignment extends BaseEntity {
  location_id: UUID;
  user_id: UUID;
  is_primary: boolean;
  assigned_at: string;
}

// Organization statistics
export interface OrganizationStats {
  total_contacts: number;
  total_locations: number;
  total_active_tasks: number;
  total_events_this_month: number;
  total_interactions_this_month: number;

  // Aliases for UI compatibility
  contacts_count?: number;
  locations_count?: number;
  documents_count?: number;
}

// Organization settings
export interface OrganizationSettings {
  [key: string]: unknown;
}

// Organization type tag (legacy - for organization_types array)
export type OrganizationTypeTag = string;

// Organization type values (single select)
export type OrganizationTypeValue =
  | "retailer"
  | "retailer_with_range"
  | "promoter"
  | "partner"
  | "range"
  | "instructor"
  | "organization"
  | "media"
  | "manufacturer"
  | "business"
  | "school_district"
  | "other";

// Organization type options for dropdowns
export const ORGANIZATION_TYPE_OPTIONS: {
  value: OrganizationTypeValue;
  label: string;
  description?: string;
}[] = [
  {
    value: "retailer",
    label: "Retailer",
    description: "Gun stores, FFL dealers, online retailers",
  },
  {
    value: "retailer_with_range",
    label: "Retailer with Range",
    description: "Retailers that also have shooting ranges",
  },
  {
    value: "range",
    label: "Range",
    description: "Shooting ranges (no retail)",
  },
  {
    value: "instructor",
    label: "Instructor / Training",
    description: "LTC, firearms, and self-defense instructors",
  },
  {
    value: "promoter",
    label: "Gun Show Promoter",
    description: "Gun show and event promoters, 2A advocates",
  },
  {
    value: "school_district",
    label: "School / District",
    description: "Schools and school districts",
  },
  {
    value: "organization",
    label: "Organization",
    description: "2A advocacy groups, associations, non-profits",
  },
  {
    value: "manufacturer",
    label: "Manufacturer",
    description: "Firearms and accessories manufacturers",
  },
  {
    value: "media",
    label: "Media",
    description: "Publications, social media, content creators",
  },
  {
    value: "partner",
    label: "Partner",
    description: "Strategic business partners",
  },
  {
    value: "business",
    label: "Business",
    description: "General business accounts",
  },
  { value: "other", label: "Other", description: "Other organization types" },
];

/**
 * Facility Type picker for the organization form (requirements §6.1 order). "FFL" and "FFL with Range" are
 * picker ALIASES: they save as retailer / retailer_with_range and display as Retailer / Retailer with Range
 * (requirements note), so map, locator and voucher eligibility are unchanged. Values not in §6.1 that are in use
 * (organization, manufacturer, media, partner, business) stay selectable.
 */
export const FACILITY_TYPE_PICKER_OPTIONS: {
  key: string;
  value: OrganizationTypeValue;
  label: string;
}[] = [
  { key: "retailer", value: "retailer", label: "Retailer" },
  {
    key: "retailer_with_range",
    value: "retailer_with_range",
    label: "Retailer with Range",
  },
  { key: "ffl", value: "retailer", label: "FFL (saved as Retailer)" },
  {
    key: "ffl_with_range",
    value: "retailer_with_range",
    label: "FFL with Range (saved as Retailer with Range)",
  },
  { key: "range", value: "range", label: "Range" },
  { key: "promoter", value: "promoter", label: "Gun Show Promoter" },
  { key: "instructor", value: "instructor", label: "Instructor / Training" },
  {
    key: "school_district",
    value: "school_district",
    label: "School / District",
  },
  { key: "other", value: "other", label: "Other" },
  { key: "organization", value: "organization", label: "Organization" },
  { key: "manufacturer", value: "manufacturer", label: "Manufacturer" },
  { key: "media", value: "media", label: "Media" },
  { key: "partner", value: "partner", label: "Partner" },
  { key: "business", value: "business", label: "Business" },
];

/** Display label for a stored organization_type (case-insensitive; unknown legacy strings shown as-is). */
export const organizationTypeLabel = (
  value: string | null | undefined,
): string => {
  if (!value) {
    return "";
  }
  const v = value.trim().toLowerCase();
  return ORGANIZATION_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? value;
};

// Organization status history record
export interface OrganizationStatusHistory {
  id: UUID;
  organization_id: UUID;
  field_changed: "status" | "is_prospect" | "organization_type";
  old_value?: string;
  new_value?: string;
  changed_by?: UUID;
  changed_at: string;
  notes?: string;
  created_at: string;
  // Joined data
  changed_by_user?: {
    id: UUID;
    full_name: string;
    email: string;
  };
}

// Linked organization (non-hierarchical relationship between orgs)
export interface OrganizationLink {
  id: string;
  organization_a_id: string;
  organization_b_id: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  linked_organization: Organization; // the "other" org, resolved by service
}

// Organization type enum (legacy - kept for compatibility)
export enum OrganizationType {
  RETAILER = "retailer",
  PROMOTER = "promoter",
  PARTNER = "partner",
  RANGE = "range",
  OTHER = "other",
}

// Geocode source type
export type GeocodeSource =
  | "zip_lookup"
  | "nominatim"
  | "google"
  | "census"
  | "manual";

// Lightweight type for map markers (avoids fetching full Organization objects)
export interface MapOrganization {
  id: string;
  name: string;
  organization_type?: OrganizationTypeValue;
  status: Status;
  is_prospect?: boolean;
  latitude: number;
  longitude: number;
  address_line1?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  phone?: string;
  distance_in_miles?: number;
}

// Lightweight type for location map markers
export interface MapLocation {
  id: string;
  organization_id: string;
  name: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  postal_code?: string;
  address_line1?: string;
  is_archived?: boolean;
}
